import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { PendingAuth, Session, User } from "@wiseowl/shared";
import { isApiError } from "@wiseowl/shared";
import { api } from "./api.js";
import { claimAnonReflection } from "./anon-reflection.js";

/*
 * Real auth context. Talks to the live worker at $VITE_API_BASE_URL.
 *
 * The OTP flow is two steps from the UI's point of view:
 *   1. requestSignUp / requestSignIn  → server issues an OTP. While
 *      no email provider is configured, the server returns `devOtp`
 *      in the response so the flow is completable end-to-end. The
 *      echo self-disables the moment EMAIL_PROVIDER_KEY is set on the
 *      worker.
 *   2. verifyCode                     → exchanges the code for a
 *      session and persists the bearer token.
 *
 * Direct password sign-in (`signInWithPassword`) skips the OTP — used
 * when the user has previously set a password.
 *
 * `isDemo` is retained for back-compat in the UI; it's always false now
 * since the env flipped to real-API mode.
 */

const TOKEN_KEY = "wo.token";

export type AuthRequestResult =
  | { ok: true; devOtp?: string }
  | { ok: false; message: string };

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isDemo: boolean;
  setUser: (u: User) => void;
  requestSignUp: (email: string, name: string, password?: string) => Promise<AuthRequestResult>;
  requestSignIn: (email: string) => Promise<AuthRequestResult>;
  requestPasswordReset: (email: string) => Promise<AuthRequestResult>;
  signInWithPassword: (email: string, password: string, rememberMe?: boolean) => Promise<string | null>;
  verifyCode: (email: string, code: string, rememberMe?: boolean) => Promise<string | null>;
  setPassword: (password: string) => Promise<string | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);
export const IS_DEMO = false;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setLoading] = useState<boolean>(true);

  // On mount, rehydrate from a saved bearer token.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }
    void api<{ user: User }>("/me").then((r) => {
      if (!isApiError(r)) setUser(r.data.user);
      else localStorage.removeItem(TOKEN_KEY);
      setLoading(false);
    });
  }, []);

  const persistSession = useCallback((session: Session) => {
    localStorage.setItem(TOKEN_KEY, session.token);
    setUser(session.user);
    // Promote any device-local anon reflection into the mood bank. Runs
    // after the token is set so the claim's API calls carry the bearer.
    void claimAnonReflection().catch(() => undefined);
  }, []);

  const requestSignUp = useCallback<AuthState["requestSignUp"]>(async (email, name, password) => {
    const body: Record<string, string> = { email: email.trim(), name: name.trim() };
    if (password) body.password = password;
    const r = await api<PendingAuth>("/auth/sign-up", { method: "POST", body: JSON.stringify(body) });
    if (isApiError(r)) return { ok: false, message: r.error.message };
    return { ok: true, devOtp: r.data.devOtp };
  }, []);

  const requestSignIn = useCallback<AuthState["requestSignIn"]>(async (email) => {
    const r = await api<PendingAuth>("/auth/sign-in", {
      method: "POST",
      body: JSON.stringify({ email: email.trim() }),
    });
    if (isApiError(r)) return { ok: false, message: r.error.message };
    return { ok: true, devOtp: r.data.devOtp };
  }, []);

  const requestPasswordReset = useCallback<AuthState["requestPasswordReset"]>(async (email) => {
    const r = await api<PendingAuth>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email: email.trim() }),
    });
    if (isApiError(r)) return { ok: false, message: r.error.message };
    return { ok: true, devOtp: r.data.devOtp };
  }, []);

  const verifyCode = useCallback<AuthState["verifyCode"]>(async (email, code, rememberMe) => {
    const r = await api<Session>("/auth/verify", {
      method: "POST",
      body: JSON.stringify({ email: email.trim(), code, rememberMe: !!rememberMe }),
    });
    if (isApiError(r)) return r.error.message;
    persistSession(r.data);
    return null;
  }, [persistSession]);

  const signInWithPassword = useCallback<AuthState["signInWithPassword"]>(async (email, password, rememberMe) => {
    const r = await api<Session>("/auth/password", {
      method: "POST",
      body: JSON.stringify({ email: email.trim(), password, rememberMe: !!rememberMe }),
    });
    if (isApiError(r)) return r.error.message;
    persistSession(r.data);
    return null;
  }, [persistSession]);

  const setPassword = useCallback<AuthState["setPassword"]>(async (password) => {
    const r = await api<{ ok: boolean }>("/auth/set-password", {
      method: "POST",
      body: JSON.stringify({ password }),
    });
    if (isApiError(r)) return r.error.message;
    return null;
  }, []);

  const signOut = useCallback(async () => {
    // Best-effort server revocation; clear local state regardless.
    await api<{ ok: boolean }>("/me/sign-out", { method: "POST" }).catch(() => undefined);
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user, isLoading, isDemo: false, setUser,
      requestSignUp, requestSignIn, requestPasswordReset,
      signInWithPassword, verifyCode, setPassword, signOut,
    }),
    [user, isLoading, requestSignUp, requestSignIn, requestPasswordReset,
      signInWithPassword, verifyCode, setPassword, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthState => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
